import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ClipboardList, PlayCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  picking: 'bg-blue-100 text-blue-800',
  picked: 'bg-purple-100 text-purple-800',
};

export default function PickList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      // Filter orders that need picking (pending or picking status)
      const pickableOrders = response.data.filter(
        (order) => order.status === 'pending' || order.status === 'picking'
      );
      setOrders(pickableOrders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load pick lists');
    } finally {
      setLoading(false);
    }
  };

  const startPicking = async (orderId) => {
    try {
      // Update order status to picking if it's pending
      const order = orders.find((o) => o.id === orderId);
      if (order && order.status === 'pending') {
        await axios.patch(`${API}/orders/${orderId}/status`, { status: 'picking' });
      }
      navigate(`/pick-execution/${orderId}`);
    } catch (error) {
      console.error('Failed to start picking:', error);
      toast.error('Failed to start picking');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pick-list-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900">Pick Lists</h1>
        <p className="text-slate-600 mt-2">Start picking orders for fulfillment</p>
      </div>

      {orders.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ClipboardList className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No pending pick lists</h3>
            <p className="text-slate-600">All orders have been picked or there are no pending orders</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Pending Pick Lists</CardTitle>
            <CardDescription>{orders.length} order(s) waiting to be picked</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id} className="table-row-hover">
                    <TableCell>
                      <code className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                        {order.order_number}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{order.customer_name}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {order.platform}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono">{order.items.length} item(s)</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`status-badge ${statusColors[order.status]}`}>
                        {order.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {new Date(order.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        onClick={() => startPicking(order.id)}
                        className="bg-[#1680e5] hover:bg-[#1680e5]/90"
                        data-testid={`start-picking-${order.order_number}`}
                      >
                        <PlayCircle className="h-4 w-4 mr-2" />
                        {order.status === 'picking' ? 'Continue' : 'Start Picking'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
